/**
 * Versatile text-input sanitizer. Defense-in-depth on top of what the
 * render/network layer already does (React auto-escapes JSX text nodes,
 * Apollo escapes GraphQL variables, `encodeURIComponent` escapes URL
 * params) — this function focuses on characters that slip through those
 * layers and either break intent silently or spoof what the user sees.
 *
 * Every input field that accepts free-form text should route through this
 * (search boxes, name fields, descriptions, emails, filters). Number inputs
 * are handled separately by `parseNumberOnly` in the shared `<Input>`; do
 * not layer both.
 *
 * ALWAYS strips (regardless of options):
 *   - C0 / C1 control characters, except `\t` `\n` `\r` — those are
 *     handled by the whitespace rules below.
 *   - Zero-width characters (ZWSP, ZWNJ, ZWJ, BOM). Invisible; the classic
 *     "why doesn't my search / login match?" trap when copy-pasted from
 *     rich text or non-latin sources.
 *   - Bidi override / isolate characters (U+202A…U+202E, U+2066…U+2069).
 *     Defuses RTL-override spoofing where an attacker crafts a display
 *     string that renders in reverse of what's stored.
 *   - `�` (Unicode replacement character) — always a signal of
 *     upstream decode failure; keeping it downstream serves no one.
 *
 * Whitespace handling — three modes via `options`:
 *   - Default (single-line):     tabs + newlines collapse to a single space.
 *   - `multiline: true`:         `\r\n` normalizes to `\n`, tabs → space,
 *                                other newlines preserved. For textareas.
 *   - `noWhitespace: true`:      every whitespace char is dropped. For
 *                                emails, handles, phone numbers, coupon
 *                                codes. Overrides `multiline`.
 *
 * Length cap:
 *   - Optional `maxLength`. Belt-and-braces on top of the HTML input's own
 *     `maxLength` attribute — also runs from programmatic paths (URL
 *     parsing, restore-from-history) where the HTML cap can't fire.
 *
 * Whitespace collapsing beyond the above (e.g. "hello   world" →
 * "hello world") is intentionally NOT done here — mid-typing whitespace
 * needs to survive so a user typing "the " and about to add more doesn't
 * feel the space vanish. Trim once at commit-time in the consumer.
 */

// The whole purpose of this module is to strip control chars and irregular
// whitespace — the lint rules that would normally flag these patterns are
// exactly the ones we're intentionally opting out of at the source.
/* eslint-disable no-control-regex, no-irregular-whitespace */
const CONTROL_CHARS_RE = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g;
const ZERO_WIDTH_RE = /[​-‍﻿]/g;
const BIDI_OVERRIDE_RE = /[\u202A-\u202E\u2066-\u2069]/g;
const REPLACEMENT_CHAR_RE = /�/g;
/* eslint-enable no-control-regex, no-irregular-whitespace */

export interface SanitizeInputOptions {
  /** Preserve newlines (`\r\n` normalizes to `\n`, tabs → space).
   *  Default: false — single-line, tabs + newlines collapse to a space. */
  multiline?: boolean;
  /** Strip ALL whitespace (spaces + tabs + newlines). Default: false.
   *  For emails / handles / codes. Overrides `multiline`. */
  noWhitespace?: boolean;
  /** Hard length cap. Default: no cap. */
  maxLength?: number;
}

export function sanitizeInput(input: string, options: SanitizeInputOptions = {}): string {
  if (!input) return '';
  let out = input
    .replace(CONTROL_CHARS_RE, '')
    .replace(ZERO_WIDTH_RE, '')
    .replace(BIDI_OVERRIDE_RE, '')
    .replace(REPLACEMENT_CHAR_RE, '');

  if (options.noWhitespace) {
    out = out.replace(/\s+/g, '');
  } else if (options.multiline) {
    out = out.replace(/\r\n?/g, '\n').replace(/\t/g, ' ');
  } else {
    out = out.replace(/[\t\r\n]+/g, ' ');
  }

  const cap = options.maxLength;
  if (cap != null && out.length > cap) out = out.slice(0, cap);
  return out;
}
